/**
 * Search and results. No keystroke touches the network for the catalogue — see
 * `entities/catalogue`.
 *
 * There is no debounce and no loading state for items, because there is nothing to wait for:
 * search is a pure function of the in-memory index, measured at under 11 ms on 3,000 variants.
 *
 * The one exception is a phone's IMEI. Four or more digits also ask the server which handsets on
 * this shelf carry them, because which phones are in stock changes with every sale on every till
 * and the offline catalogue holds items, not handsets. That one question waits for typing to
 * pause, and its answer appears above the items: tap it and that exact phone is in the sale. A
 * tester looking for "…671" in the search box found nothing, because this did not exist.
 */

import { useEffect, useRef, useState } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';

import {
  selectActiveShopId,
  useSearchAvailableUnitsQuery,
  type AvailableUnitMatch,
  type CatalogueVariant,
  type UnitId,
} from '@pos/shared';

import { useAppSelector } from '@/app/hooks';
import { useCatalogue, useCatalogueSearch } from '@/entities/catalogue';
import { EmptyState, ErrorState, Money, StockChip, cn } from '@/shared/ui';
import { toast } from '@/shared/toast';

import { useBarcodeScanner } from '@/shared/lib/useBarcodeScanner';

export type SearchPanelProps = {
  onPick: (variant: CatalogueVariant) => void;
  /** A specific handset, found by its IMEI. */
  onPickUnit: (variant: CatalogueVariant, unit: { id: UnitId; serialNo: string }) => void;
  /** Handsets already in the sale, so they are not offered twice. */
  takenUnitIds: readonly UnitId[];
  onRetrySync: () => void;
};

/** Letters and digits only, as the server compares them. */
const serialPart = (value: string): string => value.replace(/[^0-9a-z]/gi, '');

export function SearchPanel({ onPick, onPickUnit, takenUnitIds, onRetrySync }: SearchPanelProps) {
  const catalogue = useCatalogue();
  const shopId = useAppSelector(selectActiveShopId);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);

  const hits = useCatalogueSearch(query, { limit: 40 });

  // An IMEI search only once there are four digits, and only once typing pauses.
  const [serialTerm, setSerialTerm] = useState('');
  useEffect(() => {
    const part = serialPart(query);
    const next = /\d{4,}/.test(part) ? part : '';
    const timer = setTimeout(() => {
      setSerialTerm(next);
    }, next.length === 0 ? 0 : 250);
    return () => {
      clearTimeout(timer);
    };
  }, [query]);

  const { data: unitMatches } = useSearchAvailableUnitsQuery(
    serialTerm.length === 0 || shopId === null ? skipToken : { shopId, serial: serialTerm },
  );
  const phones = serialTerm.length === 0 ? [] : (unitMatches ?? []).filter((unit) => !takenUnitIds.includes(unit.id));

  const pickPhone = (unit: AvailableUnitMatch) => {
    const variant = catalogue.index.entries.find((entry) => entry.variant.id === unit.variantId)?.variant;
    if (variant === undefined) {
      toast.warning('That phone is not in this till\'s catalogue yet. Pull down to refresh, then try again.');
      return;
    }
    onPickUnit(variant, { id: unit.id, serialNo: unit.serialNo });
    setQuery('');
  };

  useBarcodeScanner({
    enabled: catalogue.status === 'ready',
    onScan: (code) => {
      const found = catalogue.index.byBarcode.get(code.trim());

      if (found === undefined) {
        setQuery(code);
        // A phone box's barcode is its IMEI: the search below looks for the handset, so there is
        // nothing to warn about yet. Anything else is named, so the salesperson can read the
        // number back off the label and check it.
        if (!/^\d{14,16}$/.test(code.trim())) {
          toast.warning(`No item found for barcode ${code}. Search by name or SKU instead.`);
        }
        inputRef.current?.focus();
        return;
      }

      onPick(found);
      setQuery('');
    },
  });

  if (catalogue.status === 'failed') {
    return (
      <ErrorState
        title="The offline catalogue could not be opened"
        body={catalogue.error ?? 'Sign out and back in to rebuild it.'}
        onRetry={onRetrySync}
        retryLabel="Rebuild catalogue"
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="sticky top-0 z-raised bg-surface-base px-4 pb-3 pt-4">
        <label className="sr-only" htmlFor="catalogue-search">
          Search the catalogue
        </label>
        {/* Capped and left-aligned: a search field the width of a two-panel surface is absurd,
            and the eye reads a field from its left edge, so that is where it stays. */}
        <div className="flex max-w-[560px] items-center gap-2 rounded-control border border-line-strong bg-surface-raised px-4 focus-within:border-action">
          <SearchIcon />
          <input
            data-tour="search"
            id="catalogue-search"
            ref={inputRef}
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
            }}
            placeholder="Search or scan"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            className="min-h-comfortable flex-1 bg-transparent text-base outline-none placeholder:text-content-muted"
          />
          {query.length > 0 && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              className="rounded-full p-1.5 text-content-muted"
            >
              <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true">
                <path d="m4 4 8 8m0-8-8 8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="scroll-region flex-1 px-4 pb-4">
        {query.length === 0 ? (
          <EmptyState
            title="Scan or start typing"
            body={
              catalogue.status === 'ready'
                ? `${catalogue.index.size} items ready to search, no connection needed.`
                : 'The catalogue is downloading. It only has to happen once.'
            }
          />
        ) : hits.length === 0 && phones.length === 0 ? (
          <EmptyState
            title={`Nothing matches “${query}”`}
            body="Try fewer words, or part of the SKU. Search covers name, brand, SKU, barcode and attributes — and a phone's IMEI, from four digits."
          />
        ) : (
          <>
            {phones.length > 0 && (
              <section aria-label="Phones by IMEI" className="mb-2">
                <p className="pb-1 pt-1 text-xs font-semibold uppercase tracking-[0.12em] text-content-muted">
                  Phones by IMEI
                </p>
                <ul className="-mx-4 divide-y divide-line-subtle">
                  {phones.map((unit) => (
                    <li key={unit.id}>
                      <PhoneRow unit={unit} fragment={serialTerm} onPick={pickPhone} />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Dividers, not cards. A card per row plus a gap between them was ~85px a result and
                five on a screen; a 64px row with a hairline is twelve. The gaps were the cost. */}
            {hits.length > 0 && (
              <ul className="-mx-4 divide-y divide-line-subtle">
                {hits.map((hit) => (
                  <li key={hit.variant.id}>
                    <ResultRow variant={hit.variant} matchedOn={hit.reason} onPick={onPick} />
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ResultRow({
  variant,
  matchedOn,
  onPick,
}: {
  variant: CatalogueVariant;
  matchedOn: 'barcode' | 'sku' | 'name' | 'fuzzy';
  onPick: (variant: CatalogueVariant) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => {
        onPick(variant);
      }}
      className={cn(
        // 64px: 12px padding, two 20px lines. Name and price on the first, SKU and stock on the
        // second, so the eye reads the row left-to-right once rather than scanning two columns.
        // 64px exactly. `h-16` is 4rem, which on this app's 17px root is 68 — the spec names pixels.
        'flex h-[64px] w-full flex-col justify-center gap-0.5 px-4 text-left',
        'transition-colors duration-instant ease-springSubtle active:bg-surface-sunken',
      )}
    >
      <span className="flex items-baseline justify-between gap-3">
        <span className="truncate text-base font-semibold leading-6 text-content-primary">{variant.displayName}</span>
        <Money kes={variant.retailKes} size="base" className="shrink-0" />
      </span>
      <span className="flex h-5 items-center justify-between gap-3 text-xs leading-5 text-content-muted">
        <span className="truncate">
          <span data-nums="id">{variant.sku}</span>
          {matchedOn === 'fuzzy' && <span> · close match</span>}
        </span>
        <StockChip quantity={variant.quantity} threshold={variant.lowStockThreshold} size="sm" />
      </span>
    </button>
  );
}

/** One handset: the item, and its IMEI with the typed digits picked out. */
function PhoneRow({
  unit,
  fragment,
  onPick,
}: {
  unit: AvailableUnitMatch;
  fragment: string;
  onPick: (unit: AvailableUnitMatch) => void;
}) {
  const at = unit.serialNo.toLowerCase().indexOf(fragment.toLowerCase());

  return (
    <button
      type="button"
      onClick={() => {
        onPick(unit);
      }}
      className={cn(
        'flex h-[64px] w-full flex-col justify-center gap-0.5 px-4 text-left',
        'transition-colors duration-instant ease-springSubtle active:bg-surface-sunken',
      )}
    >
      <span className="truncate text-base font-semibold leading-6 text-content-primary">{unit.displayName}</span>
      <span className="text-xs leading-5 text-content-muted" data-nums="id">
        {unit.serialLabel ?? 'IMEI'}{' '}
        {at < 0 ? (
          unit.serialNo
        ) : (
          <>
            {unit.serialNo.slice(0, at)}
            <strong className="font-semibold text-content-primary">{unit.serialNo.slice(at, at + fragment.length)}</strong>
            {unit.serialNo.slice(at + fragment.length)}
          </>
        )}
        {' · tap to add this phone'}
      </span>
    </button>
  );
}

function SearchIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" className="text-content-muted">
      <circle cx="11" cy="11" r="6.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="m16 16 4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
